<template>
  <!-- 普通商品组件 -->
  <div @click="actProductFn" v-if="type === 'info' || type === 'cms' || type === 'promotion' || type === 'recent-view'" class="product-item">
    <div class="product-image">
      <template v-if="notImgLazy">
        <img :src="$cvWebpFn(productData.image)" :lazy="useImgLazy().value" :alt="productData.title" />
        <div class="ads-mark" v-if="isAds">Sponsored</div>
      </template>

      <ClientOnly v-else>
        <img v-lazy="$cvWebpFn(productData.image)" :alt="productData.title" />
        <div class="ads-mark" v-if="isAds">Sponsored</div>
        
        <template #fallback>
          <img :src="useImg1pxBase64().value" lazy="loading" alt="" >
        </template>
      </ClientOnly>
    </div>
    <div class="product-info">
      <div class="product-name">
        <img
          v-if="showChoiceTag"
          class="choice-tag"
          :src="$cvWebpFn(CHOICE_BADGE_URL)"
          alt="Kilimall Choice"
        />
        <img
          v-if="showNewTag"
          class="new-tag"
          :src="$cvWebpFn(NEW_BADGE_URL)"
          alt="New"
        />
        <img
          v-if="showFlashSaleTag"
          class="flash-sale-tag"
          :src="$cvWebpFn(FLASH_SALE_BADGE_URL)"
          alt="Flash Sale"
        />
        <img
          v-for="badge in promoBadges"
          :key="badge.key"
          class="promo-tag"
          :src="$cvWebpFn(badge.url)"
          :alt="badge.alt"
        />
        {{ productData.title }}
      </div>
        <div class="product-price clerafix">
          <div class="price-main">{{ $currencySymbol() }} {{ $toThousandsFn(productData.minPrice) }}</div>
          <div v-if="type === 'cms'" class="price-sec">{{ $currencySymbol() }} {{ $toThousandsFn(productData.delPrice || productData.minListPrice) }}</div>
          <div class="rate" v-if="showRate">
            <van-rate class="review-star-icon" color="#F9BE00" :model-value="productData.reviewStar || 5" />
            <span class="reviews">({{ productData.reviews }})</span>
          </div>
        </div>

        <div class="mark-box">
          <img
            v-if="showBrandOfficial"
            class="store-tag"
            :src="$cvWebpFn(OFFICIAL_STORE_BADGE_URL)"
            alt="Brand Official"
          />
          <img
            v-if="showBrandAuthorized"
            class="store-tag"
            :src="$cvWebpFn(AUTHORIZED_STORE_BADGE_URL)"
            alt="Brand Authorized"
          />
          <img
            v-if="showPreferred"
            class="store-tag"
            :src="$cvWebpFn(PREFERRED_STORE_BADGE_URL)"
            alt="Preferred Store"
          />
          <template v-for="(tag, idx) in productData.logisticsTypes" :key="idx">
            <img
              v-if="getLogisticsBadgeUrl(tag.name)"
              class="logistics-tag"
              :src="$cvWebpFn(getLogisticsBadgeUrl(tag.name))"
              :alt="tag.name"
            />
            <div v-else class="logistics-tag logistics-tag--text">
              <span class="tag-name" :style="{ color: tag.color }">{{ tag.name }}</span>
              <span class="tag-bg" :style="{ background: tag.color, opacity: 0.2 }"></span>
            </div>
          </template>
        </div>
    </div>

    <div v-if="type === 'promotion'" class="add-cart" @click.stop="handleAddCartClick">
      <van-icon name="cart-o" size="18px" color="#DD3131" />
    </div>
  </div>
  <!-- 秒杀商品组件 -->
  <div @click="actProductFn" v-if="type === 'flash-sale' || type === 'new-arrival' " class="flash-sale-product">
    <div class="product-image">
      <ClientOnly>
        <img v-lazy="$cvWebpFn(productData.image)" :alt="productData.title" />

        <template #fallback>
          <img :src="useImg1pxBase64().value" alt="" lazy="loading">
        </template>
      </ClientOnly>
    </div>
    <div class="product-price" :style="{ color: type === 'new-arrival' ? '#150000' : '#DD3131' }">
      {{ $currencySymbol() }}
      {{
        $toThousandsFn(
          productData.activityPrice ||
          productData.listPrice ||
          productData.minPrice ||
          productData.price
        )
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { FLASH_SALE_BADGE_URL, getProductTrafficSourceTracking, parseTrafficSourceTrackingToListingQuery, RECENT_VIEW_LATEST_TRAFFIC_SOURCE_PAGE, resolveIsFlashSaleTag, resolvePromoBadges } from '@/plugins/utils'

const router = useRouter()
const props = withDefaults(defineProps<{
  productData: any
  type?: 'flash-sale' | 'new-arrival' | 'info' | 'default' | 'cms' | 'promotion' | 'recent-view'
  isAds?: boolean
  // 埋点来源
  source?: string
  // 图片不进行懒加载
  notImgLazy?: boolean
}>(), {
  type: 'info'
})

const emits = defineEmits<{(e: 'add-cart:click', data: any): void}>()

const CHOICE_BADGE_URL = 'https://image.kilimall.com/c/h5/KiliChoice.png'
const NEW_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_new_goods.png'
const OFFICIAL_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_official_store.png'
const AUTHORIZED_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_authorized_store.png'
const PREFERRED_STORE_BADGE_URL = 'https://image.kilimall.com/c/h5/ic_preferred_storex.png'
const LOGISTICS_BADGE_URLS: Record<string, string> = {
  'local dispatch': 'https://image.kilimall.com/c/h5/ic_local_dispatch.png',
  fbk: 'https://image.kilimall.com/c/h5/ic_fbk.png',
  'fulfilled by kilimall': 'https://image.kilimall.com/c/h5/ic_fbk.png',
  'oversea shipping': 'https://image.kilimall.com/c/h5/ic_overseas_shipping.png',
  'oversea shipment': 'https://image.kilimall.com/c/h5/ic_overseas_shipping.png'
}

const { $toListingDetail } = useNuxtApp()
const showRate = computed(() => {
  return Object.keys(props.productData).includes('reviewStar') && props.type !== 'cms'
})
const showBrandOfficial = computed(() => {
  // storeAuthType: 1 官方店铺
  return props.productData?.storeAuthType === 1
})
const showBrandAuthorized = computed(() => {
  // storeAuthType: 2 品牌授权
  return props.productData?.storeAuthType === 2
})

const showPreferred = computed(() => props.productData?.tags?.includes('store_preferred'))

const getLogisticsBadgeUrl = (name?: string) =>
  LOGISTICS_BADGE_URLS[name?.trim().toLowerCase() || '']

const showNewTag = computed(() => {
  return Array.isArray(props.productData?.highlights) && props.productData.highlights.includes('NEW')
})

const showChoiceTag = computed(() => {
  return Array.isArray(props.productData?.highlights)
    && props.productData.highlights.some((item: string) => item.includes('Choice'))
})

const showFlashSaleTag = computed(() =>
  resolveIsFlashSaleTag(props.productData?.highlights)
)

const promoBadges = computed(() =>
  resolvePromoBadges(props.productData?.highlights)
)

const getFlashSaleSkuId = () =>
  Number(props.productData?.defaultSkuId || props.productData?.skuId || 0)

/**
 * 秒杀 and 其他商品
 */
const actProductFn = () => {
  if(props.type === 'flash-sale') {
    router.push('/flash-sales?source=homePage|mainIcon|flashSale')
  } else {
    const { title, listingId, defaultSkuId, shortTitle, requestId, algorithmModelName } = props.productData
    const trackingQuery = parseTrafficSourceTrackingToListingQuery(
      getProductTrafficSourceTracking(props.productData),
      props.type === 'recent-view'
        ? { latestTrafficSourcePage: RECENT_VIEW_LATEST_TRAFFIC_SOURCE_PAGE }
        : undefined
    )

    const detailQuery = {
      title,
      shortTitle,
      source: props.source,
      isAd: props.isAds ? 'ad' : undefined,
      isNewTag: showNewTag.value,
      isChoiceTag: showChoiceTag.value,
      requestId: requestId || '',
      algorithmModelName: algorithmModelName || '',
      ...trackingQuery
    }

    const flashSkuId = getFlashSaleSkuId()
    if (showFlashSaleTag.value && flashSkuId > 0) {
      $toListingDetail(flashSkuId, {
        ...detailQuery,
        from: 'flash-sale'
      })
      return
    }

    $toListingDetail(
      listingId,
      {
        ...detailQuery,
        skuId: defaultSkuId
      }
    )
  }
}

const handleAddCartClick = () => {
  emits('add-cart:click', props.productData)
}
</script>

<style lang='scss' scoped>
.product-item {
  width: 100%;
  color: var(--font_color_title);
  background-color: white;
  border-radius: 4px;
  overflow: hidden;
  height: 100%;
  position: relative;
  .product-image {
    width: 100%;
    height: 0;
    overflow: hidden;
    padding-top: 100%;
    position: relative;
    img {
      width: 100%!important;
      height: auto;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      -o-object-fit: cover;
      object-fit: cover;
    }
    .ads-mark{
      position: absolute;
      color: #F7F7F7;
      font-size: 22px;
      padding: 4px 8px;
      background: black;
      border-radius: 4px 0 0 0;
      right: 0px;
      bottom: 0px;
      opacity: 20%;
    }
  }
  .product-info {
    padding: 10px 16px;
    .product-name {
      color: var(--font_color_title);
      overflow: hidden;
      font-size: 28px;
      // line-height: 40px;
      margin: 8px 0;
      @include ellipsisMultiline(2);
      .choice-tag {
        display: inline-block;
        height: 28px;
        width: auto;
        margin-right: 10px;
        vertical-align: middle;
        margin-top: -2px;
      }
      .flash-sale-tag,
      .promo-tag {
        display: inline-block;
        height: 28px;
        width: auto;
        margin-right: 10px;
        vertical-align: middle;
        margin-top: -2px;
      }
      .new-tag {
        display: inline-block;
        width: auto;
        height: 30px;
        margin-right: 10px;
        vertical-align: middle;
        margin-top: -2px;
      }
    }
    .product-price {
      margin-top: 10px;
      line-height: 40px;
      .price-main {
        font-weight: 600;
        float: left;
        margin-right: 20px;
        font-size: 30px;
        color: #DD3131
      }
      .price-sec {
        color: #999999;
        font-size: 22px;
        font-weight: 100;
        text-decoration: line-through;
        @include center();
        float: left;
      }
    }
    .rate{
      width: 100%;
      display: flex;
      align-items: center;
      color: #999999;
      .reviews{
        font-size: 22px;
        display: inline-block;
        margin-left: 10px;
      }
    }

    .mark-box{
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding-right: 30px;
    }
    .store-tag {
      display: block;
      width: auto;
      height: 24px;
      margin-top: 12px;
    }
    .logistics-tag{
      display: block;
      width: auto;
      height: 24px;
      margin-top: 12px;
      &--text {
        overflow: hidden;
        position: relative;
        width: auto;
        height: auto;
        border-radius: 6px;
        padding: 2px 5px;
        color: #fff;
        font-size: 16px;
        .tag-name{
          position: relative;
          z-index: 1;
          white-space: nowrap;
        }
        .tag-bg{
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
  .add-cart{
    position: absolute;
    right: 6px;
    bottom: 6px;
    background-color: #faebea;
    border-radius: 50%;
    height: 55px;
    width: 55px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.flash-sale-product {
  width: 100%;
  .product-image {
    width: 100%;
    height: 0;
    border-radius: 16px;
    overflow: hidden;
    padding-top: 100%;
    position: relative;
    img {
      width: 100%!important;
      height: auto;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      -o-object-fit: cover;
      object-fit: cover;
    }
  }
  .product-price {
    color: var(--default_color);
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    margin-top: 10px;
  }
}
.review-star-icon::v-deep(.van-rate__icon){
  font-size: 26px !important;
}
</style>
